import { useEffect, useMemo, useRef, useState } from "react";
import { Search, X, Check } from "lucide-react";
import { useServices } from "@/lib/data";
import { searchServices } from "@/lib/search";
import { ServiceIcon } from "@/lib/icons";

const PAGE = 40;

function buildRows(services, query) {
  if (query.trim()) return searchServices(services, query).map((s) => ({ type: "item", s, showGroup: true }));
  const rows = [];
  let last = null;
  for (const s of services) {
    if (s.group !== last) { rows.push({ type: "header", group: s.group }); last = s.group; }
    rows.push({ type: "item", s });
  }
  return rows;
}

export function ServiceList({ query, onPick, selected = [], testId = "service-list" }) {
  const { data: services = [] } = useServices();
  const rows = useMemo(() => buildRows(services, query), [services, query]);
  const [count, setCount] = useState(PAGE);
  const sentinel = useRef(null);
  useEffect(() => setCount(PAGE), [query]);
  useEffect(() => {
    const el = sentinel.current;
    if (!el) return undefined;
    const io = new IntersectionObserver((e) => e[0].isIntersecting && setCount((c) => c + PAGE));
    io.observe(el);
    return () => io.disconnect();
  }, [rows.length, count]);

  if (!rows.length) return <p className="p-4 text-lg text-slate-600" data-testid={`${testId}-empty`}>Nenhum serviço encontrado.</p>;
  return (
    <ul data-testid={testId}>
      {rows.slice(0, count).map((r) => r.type === "header" ? (
        <li key={`h-${r.group}`} className="sticky top-0 z-10 bg-slate-100 px-4 py-2 text-sm font-bold uppercase tracking-wide text-slate-600">{r.group}</li>
      ) : (
        <li key={r.s.slug}>
          <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => onPick(r.s)} data-testid={`service-option-${r.s.slug}`}
            className="flex min-h-[56px] w-full items-center gap-3 px-4 text-left text-lg text-slate-800 hover:bg-orange-50 focus:bg-orange-50 focus:outline-none">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-orange-100 text-orange-600"><ServiceIcon icon={r.s.icon} className="h-5 w-5" /></span>
            <span className="flex-1">{r.s.name}{r.showGroup && <span className="block text-sm text-slate-500">{r.s.group}</span>}</span>
            {selected.includes(r.s.slug) && <Check className="h-6 w-6 text-orange-600" />}
          </button>
        </li>
      ))}
      {count < rows.length && <li ref={sentinel} className="p-3 text-center text-base text-slate-500">Carregando mais...</li>}
    </ul>
  );
}

export function ServiceSearchBox({ onPick, selected = [], placeholder = "Digite: eletricista, diarista, frete...", testId = "service-search", keepOpen = false }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const pick = (s) => { onPick(s); if (!keepOpen) { setOpen(false); setQ(""); } };
  return (
    <div className="relative">
      <Search className="pointer-events-none absolute left-4 top-[18px] h-6 w-6 text-slate-400" />
      <input value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={placeholder} className="po-input pl-12 pr-12" aria-label="O que você precisa?" data-testid={`${testId}-input`} />
      {q && <button type="button" onClick={() => setQ("")} className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full hover:bg-slate-100" aria-label="Limpar"><X className="h-5 w-5" /></button>}
      {open && (
        <div className="absolute left-0 right-0 z-[500] mt-2 max-h-[360px] overflow-y-auto rounded-2xl border-2 border-slate-200 bg-white shadow-2xl" data-testid={`${testId}-dropdown`}>
          <ServiceList query={q} onPick={pick} selected={selected} testId={`${testId}-list`} />
        </div>
      )}
    </div>
  );
}

export function SelectedService({ service, onClear, testId = "selected-service" }) {
  return (
    <div className="flex min-h-[64px] items-center gap-3 rounded-2xl border-2 border-orange-500 bg-orange-50 px-4" data-testid={testId}>
      <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-orange-500 text-white"><ServiceIcon icon={service.icon} className="h-6 w-6" /></span>
      <span className="flex-1"><span className="block text-xl font-bold text-slate-900">{service.name}</span><span className="text-sm text-slate-600">{service.group}</span></span>
      <button type="button" onClick={onClear} className="po-btn-outline min-h-[48px] px-4 text-base" data-testid={`${testId}-clear`}>Trocar</button>
    </div>
  );
}

export function ServicePicker({ value, onChange, testId = "service-picker" }) {
  const { data: services = [] } = useServices();
  const current = services.find((s) => s.slug === value);
  if (current) return <SelectedService service={current} onClear={() => onChange("")} testId={`${testId}-selected`} />;
  return <ServiceSearchBox onPick={(s) => onChange(s.slug)} testId={testId} />;
}
